import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowRight, Gauge } from 'lucide-react';
import { conversionEfficiency } from '../../lib/leiConditions';

/**
 * Journey Conversion Efficiency (Rec 8) — the Missing Middle in one
 * number: how effectively capability conditions convert into value
 * conditions. Not a new metric, just a lens (Value ÷ Capability).
 */
export function ConversionEfficiencyCard({ conditions }) {
  const cap = conditions?.find((c) => c.key === 'capability');
  const val = conditions?.find((c) => c.key === 'value');
  const eff = conversionEfficiency(conditions);
  if (eff == null) return null;

  const tone = eff >= 90 ? 'text-emerald-600' : eff >= 75 ? 'text-amber-600' : 'text-rose-600';

  return (
    <Card data-testid="conversion-efficiency-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="h-4 w-4 text-cyan-700" />
          Journey Conversion Efficiency
        </CardTitle>
        <p className="text-xs text-slate-500">How effectively are we converting capability conditions into value?</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-2">
          <div className="text-center flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Capability</p>
            <p className="text-3xl font-extrabold tabular-nums text-slate-900">{Math.round(cap.score)}</p>
            <p className="text-[10px] text-slate-400 truncate">{cap.pillarShortName}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
          <div className="text-center flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Value</p>
            <p className="text-3xl font-extrabold tabular-nums text-slate-900">{Math.round(val.score)}</p>
            <p className="text-[10px] text-slate-400 truncate">{val.pillarShortName}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
          <div className="text-center flex-1 rounded-lg bg-slate-900 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-300">Conversion</p>
            <p className={`text-3xl font-extrabold tabular-nums ${tone === 'text-rose-600' ? 'text-rose-300' : tone === 'text-amber-600' ? 'text-amber-300' : 'text-emerald-300'}`} data-testid="conversion-efficiency-value">
              {eff}%
            </p>
            <p className="text-[10px] text-slate-400">{Math.round(val.score)} ÷ {Math.round(cap.score)}</p>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-400">
          Capability without conversion is unrealised value — this is the Missing Middle, quantified.
        </p>
      </CardContent>
    </Card>
  );
}
